import { useState, useMemo, type FC } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useContactRequestContext, type Company } from './ContactRequestContext'
import { StepContainer } from './components/StepContainer'
import { NumericInput } from '../../FormUtils/NumericInput'
import { ClientDataTable } from '../../ClientDataTable/ClientDataTable'
import { Tabs } from '../../FbUI/Tabs'

// Form schema for contact request
const formSchema = z.object({
  max_contacts: z
    .number()
    .min(1, { message: 'Please enter a number between 1 and 5.' })
    .max(5, { message: 'Please enter a number between 1 and 5.' }),
})

type FormValues = z.infer<typeof formSchema>

const TABLE_COLUMNS = [
  { header: 'Company', accessorKey: 'name' },
  { header: 'Contact Request Status', accessorKey: 'status' },
]

const toTableRow = (company: Company) => ({
  id: company.id,
  name: company.name,
  status:
    company.status ||
    (company.last_requested_date
      ? 'In Progress'
      : company.last_found_date
        ? 'Has Contacts'
        : 'New Request'),
})

export const ConfirmStep: FC = () => {
  const {
    loading,
    error,
    getSelectedCompaniesInProgress,
    getSelectedCompaniesWithContacts,
    companiesInProgress,
    companiesWithContacts,
    campaignCompanies,
    handleClose,
    prevStep,
    nextStep,
    setMaxContactsPerCompany,
    setSelectedCompanyIds,
    isMultiType,
    subcategories,
  } = useContactRequestContext()

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      max_contacts: 1,
    },
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  })

  // Get selected companies from previous steps - memoize to prevent recalculation on every render
  const selectedCompaniesInProgress = useMemo(
    () => getSelectedCompaniesInProgress(),
    [getSelectedCompaniesInProgress, companiesInProgress]
  )

  const selectedCompaniesWithContacts = useMemo(
    () => getSelectedCompaniesWithContacts(),
    [getSelectedCompaniesWithContacts, companiesWithContacts]
  )

  // Get IDs of companies that were NOT selected in previous steps - memoize to prevent recalculation
  const unselectedCompanyIds = useMemo(() => {
    const unselectedIds = new Set([
      ...companiesInProgress
        .filter((company) => !company.selected)
        .map((company) => company.id),
      ...companiesWithContacts
        .filter((company) => !company.selected)
        .map((company) => company.id),
    ])

    return unselectedIds
  }, [companiesInProgress, companiesWithContacts])

  // Derive final companies list from campaign companies and previous step selections
  const finalCompanies = useMemo<Company[]>(() => {
    if (campaignCompanies.length === 0) return []

    // Filter out companies that were explicitly unselected in previous steps
    const filteredCompanies = campaignCompanies.filter(
      (company) => !unselectedCompanyIds.has(company.id)
    )

    // Create a map of all companies from previous steps for quick lookup
    const previousCompaniesMap = new Map<number, Company>()
    selectedCompaniesInProgress.forEach((company) =>
      previousCompaniesMap.set(company.id, company)
    )
    selectedCompaniesWithContacts.forEach((company) =>
      previousCompaniesMap.set(company.id, company)
    )

    // Combine selected companies with filtered campaign companies
    const combinedCompanies = [
      ...selectedCompaniesInProgress,
      ...selectedCompaniesWithContacts,
      // Add campaign companies that weren't in previous steps
      ...filteredCompanies.filter(
        (company) => !previousCompaniesMap.has(company.id)
      ),
    ]

    // Remove duplicates by ID
    return Array.from(
      new Map(
        combinedCompanies.map((company) => [company.id, company])
      ).values()
    )
  }, [
    campaignCompanies,
    selectedCompaniesInProgress,
    selectedCompaniesWithContacts,
    unselectedCompanyIds,
  ])

  // Group companies by sub_category_name for multi-type display
  const groupedCompanies = useMemo(() => {
    if (!isMultiType) return null
    const groups: Record<string, typeof finalCompanies> = {}
    for (const company of finalCompanies) {
      const type = company.sub_category_name || 'Other'
      if (!groups[type]) groups[type] = []
      groups[type].push(company)
    }
    return groups
  }, [finalCompanies, isMultiType])

  const typeNames = useMemo(
    () => (groupedCompanies ? Object.keys(groupedCompanies) : []),
    [groupedCompanies]
  )
  // Derive active type — default to first type, user can switch via tabs
  const [activeTypeOverride, setActiveType] = useState<string | null>(null)
  const activeType =
    activeTypeOverride && typeNames.includes(activeTypeOverride)
      ? activeTypeOverride
      : (typeNames[0] ?? '')

  const typeTabs = useMemo(
    () =>
      typeNames.map((name) => ({
        key: name,
        title: `${name} (${groupedCompanies?.[name]?.length ?? 0})`,
      })),
    [typeNames, groupedCompanies]
  )

  // Find excluded (non-eligible) types for the note
  const excludedTypes = useMemo(() => {
    if (!isMultiType || !subcategories) return []
    return subcategories.filter((sc) => !sc.contact_request_allowed)
  }, [isMultiType, subcategories])

  const onSubmit = (data: FormValues) => {
    // Store the max contacts in context
    setMaxContactsPerCompany(data.max_contacts)

    // Store the selected company IDs in context
    const companyIds = finalCompanies.map((company) => company.id)
    setSelectedCompanyIds(companyIds)

    // Move to the summary step
    nextStep()
  }

  return (
    <StepContainer
      title="Request Contacts"
      subtitle={`You're requesting contacts for ${finalCompanies.length} companies. Please specify how many contacts you'd like to request per company.`}
      error={error}
      handleClose={handleClose}
      onBack={prevStep}
      onContinue={handleSubmit(onSubmit)}
      continueButtonText="Review & Confirm"
      canContinue={isValid && !loading && finalCompanies.length > 0}
      loading={loading}
      currentStep={3}
      totalSteps={4}
    >
      {finalCompanies.length > 0 ? (
        <>
          <div className="font-normal text-fg-secondary mb-2">
            <strong>Note:</strong> Contact requests are not available for
            companies outside First Bite&apos;s database, so any custom
            companies will be removed from this request.
          </div>
          <div className="mb-4">
            {groupedCompanies && typeTabs.length > 1 ? (
              // Multi-type: render tabs per opp type
              <>
                <Tabs
                  tabs={typeTabs}
                  selectedTab={activeType}
                  setSelectedTab={setActiveType}
                  isSmall
                />
                <div className="mt-3">
                  {activeType && groupedCompanies[activeType] && (
                    <ClientDataTable
                      columns={TABLE_COLUMNS}
                      data={groupedCompanies[activeType].map(toTableRow)}
                    />
                  )}
                </div>
              </>
            ) : (
              // Single-type: flat list
              <>
                <h3 className="text-sm font-medium mb-2">
                  Included Companies ({finalCompanies.length})
                </h3>
                <ClientDataTable
                  columns={TABLE_COLUMNS}
                  data={finalCompanies.map(toTableRow)}
                />
              </>
            )}
            {excludedTypes.length > 0 && (
              <p className="mt-3 text-xs text-fg-secondary">
                {excludedTypes.reduce((sum, t) => sum + t.company_count, 0)}{' '}
                companies from {excludedTypes.map((t) => t.name).join(', ')}{' '}
                excluded — contact requests not available for{' '}
                {excludedTypes.length === 1 ? 'this type' : 'these types'}.
              </p>
            )}
          </div>

          <form>
            <NumericInput
              label="How many contacts per company?"
              control={control}
              fieldName="max_contacts"
            />
          </form>
        </>
      ) : (
        <div className="text-center py-6">
          <p className="text-fg-muted">
            No companies selected. Please go back and select companies.
          </p>
        </div>
      )}
    </StepContainer>
  )
}
